import {
  CustomerBusinessUnitData,
  CustomerFormData,
} from "@components/request-access/customer-model";
import { useEffect, useState } from "react";

import AlertDialog from "@components/dialog/AlertDialog";
import Box from "@mui/material/Box";
import { BusinessInformationStep } from "@components/request-access/customer/BusinessInformationStep";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import { CustomerBusinessUnitStep } from "@components/request-access/customer/CustomerBusinessUnitStep";
import { CustomerConfirmationStep } from "@components/request-access/customer/CustomerConfirmationStep";
import { CustomerInformationStep } from "@components/request-access/customer/CustomerInformationStep";
import { CustomerRequestReasonStep } from "@components/request-access/customer/CustomerRequestReasonStep";
import { CustomerRequestedPortalStep } from "@components/request-access/customer/CustomerRequestedPortalStep";
import { GetStaticPropsContext } from "next";
import Head from "next/head";
import MobileStepper from "@mui/material/MobileStepper";
import { RequestReason } from "@components/request-access/sharedTypes";
import { RequestSent } from "@components/request-access/RequestSent";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Stepper from "@mui/material/Stepper";
import { Wrapper } from "@components/request-access/Wrapper";
import { getProductPartnerPortal } from "@components/request-access/product-partner-portal-url";
import { getRequestAccessEndpoint } from "@components/request-access/request-access-api";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { useAlertDialog } from "@hooks/useAlertDialog";
import { useCustomerAccessFormik } from "@components/request-access/useCustomerAccessFormik";
import { useCustomerBusinessFormik } from "@components/request-access/useCustomerBusinessFormik";
import { useCustomerBusinessUnitFormik } from "@components/request-access/useCustomerBusinessUnitFormik";
import { useCustomerRequestedPortalFormik } from "@components/request-access/useCustomerRequestedPortalFormik";
import { useCustomerSummaryFormik } from "@components/request-access/useCustomerSummaryFormik";
import { useGetStepsList } from "@components/request-access/useGetStepsList";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useRequestedReasonFormik } from "@components/request-access/useRequestedReasonFormik";
import { useRouter } from "next/navigation";
import { useStepManager } from "../../lib/stepper/useStepManager";
import { useTranslation } from "next-i18next";

export default function RequestAccessCustomerPage() {
  const { t } = useTranslation();
  const router = useRouter();
  const {getStepsList} = useGetStepsList();

  const [reasonForRequestData, setReasonForRequestData] = useState<
    RequestReason[]
  >([]);

  const [selectedReasons, setSelectedReasons] = useState<RequestReason[]>([]);

  const [restrictedDomains, setRestrictedDomains] = useState<string[]>([]);



  const {
    dialogOpen,
    handleOpenDialog,
    handleCloseDialog,
    handleAgreeDialog,
  } = useAlertDialog();

  const requestedPortalFormik = useCustomerRequestedPortalFormik(() => {
    let url = "";
    switch (requestedPortalFormik.values.appRefName) {
      case "GPS":
      case "DCP":
      case "Australia":
        url = getProductPartnerPortal(
          requestedPortalFormik.values.appRefName,
          requestedPortalFormik.values.country.split("|")[1]
        );
        handleOpenDialog(url); // Open the dialog here
        break;
      default:
        handleComplete();
        break;
    }
  });

  const customerInformationFormik = useCustomerAccessFormik(() => {
    handleComplete();
  });

  const requestedReasonFormik = useRequestedReasonFormik(() => {
    handleComplete();
  });

  const customerBusinessUnitFormik = useCustomerBusinessUnitFormik(() => {
    handleComplete();
  });

  const customerBusinessFormik = useCustomerBusinessFormik(() => {
    handleComplete();
  });

  const customerSummaryFormik = useCustomerSummaryFormik(() => {
    handleSubmission();
  });

  const steps: string[] = getStepsList(requestedPortalFormik.values.appRefName);

  const {
    activeStep,
    completed,
    allStepsCompleted,
    handleComplete,
    handleBack,
    handleSetStep,
  } = useStepManager(steps);

  async function handleSubmission() {
    // TODO consider loading indicators
    const result = await submitRequest();
    if (result) {
      handleComplete();
    } else {
      // TODO error handling
      // what should we present to the user
      // they will still be on step 3
      // so we will need a way to send data down to that component,
      // or have it bump down
    }
  }

  async function submitRequest() {
    const frontEndHostName =
      typeof window !== "undefined" ? window.location.host : "ssr";

    const form: CustomerFormData = getFormData();

    const payload: CustomerFormData & {
      frontEndHostName: string;
      accessType: "currentCustomer";
    } = {
      ...form,
      frontEndHostName: frontEndHostName,
      accessType: "currentCustomer",
    };

    console.info("🛫 sending customer request access payload", payload);
    const result = await postRequest(payload);

    if (result) {
      console.info("submit request successful");
      return true;
    }

    console.info("submit request failure");
    return false;
  }

  function getFormData() {
    const { requestReasons, otherReason } = requestedReasonFormik.values;
    const formatedReasons: string[] = requestReasons.map((e) => e.reason);

    const businessUnitData: CustomerBusinessUnitData = {
      businessUnits: customerBusinessUnitFormik.values.businessUnits,
      soldToShipTo: customerBusinessUnitFormik.values.soldToShipTo,
    };

    const formData: CustomerFormData = {
      ...customerInformationFormik.values,
      requestReasons: formatedReasons,
      ...customerBusinessFormik.values,
      ...requestedPortalFormik.values,
      accessReason: otherReason,
      ...customerSummaryFormik.values,
      ...businessUnitData,
    };

    return formData;
  }

  async function postRequest(payload = {}) {
    const postEndpoint = getRequestAccessEndpoint();
    const response = await fetch(postEndpoint, {
      method: "POST",
      mode: "cors",
      cache: "no-cache",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      referrerPolicy: "no-referrer",
      body: JSON.stringify(payload),
    });
    const result = response.status >= 200 && response.status <= 204;
    return result;
  }

  const checkConditionalStep = (
    appName: string,
    targetStep: number,
    defaultStep: number,
    isUnique: boolean
  ) => {
    const selectedAppName = requestedPortalFormik.values.appRefName;
    //check if appname === current name
    if (isUnique) {
      return selectedAppName === appName && targetStep === activeStep;
    } else {
      if (selectedAppName === appName) {
        return targetStep === activeStep;
      } else {
        return defaultStep === activeStep;
      }
    }
  };

  const matches = useMediaQuery("(min-width:600px)");

  useEffect(() => {
    setSelectedReasons([]);
    requestedReasonFormik.setFieldValue("requestReasons", []);
    // TODO: fix this hook dependency inconsistency
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reasonForRequestData]);

  useEffect(() => {
    customerInformationFormik.setFieldValue(
      "restrictedEmailDomains",
      restrictedDomains
    );
    // TODO: fix this hook dependency inconsistency
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restrictedDomains]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [activeStep]);

  return (
    <>
      <Head>
        <title>{`myCargill - ${t(
          "request_access:select_access_type.current_customer_title"
        )} - ${t("common:header.get_access")}`}</title>
        <meta
          name="description"
          content={t("common:header.get_access") || undefined}
        />
      </Head>
      <AlertDialog
        dialogId="alertdialog"
        open={dialogOpen}
        onClose={handleCloseDialog}
        onAgree={handleAgreeDialog}
        title={t("request_access:dialogs.redirect_title") || ""}
        contextText={t("request_access:dialogs.redirect_content")}
        cancelActionText={t("common:common.cancel")}
        continueActionText={t("common:common.continue")}
      />
      <Wrapper
        top={
          <Container
            sx={{
              display: "flex",
              alignItems: "center",
              minHeight: "3rem",
              justifyContent: "center",
            }}
          >
            {matches ? (
              <Stepper activeStep={activeStep} sx={{ width: "100%" }}>
                {steps.map((label, index) => (
                  <Step key={label} completed={completed[index]}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            ) : (
              <MobileStepper
                variant="dots"
                steps={steps.length}
                position="static"
                activeStep={activeStep}
                nextButton={null}
                backButton={null}
              />
            )}
          </Container>
        }
      >
        {allStepsCompleted() ? (
          <>
            <RequestSent />
          </>
        ) : (
          <Box sx={{ width: "100%" }}>
            <Box sx={{ mt: 2, mb: 1, py: 1 }}>
              {/* put conditional content in this box */}
              {/* first step screen */}
              {activeStep === 0 ? (
                <>
                  <CustomerRequestedPortalStep
                    setReasonForRequestData={setReasonForRequestData}
                    setRestrictedDomains={setRestrictedDomains}
                    formik={requestedPortalFormik}
                  />
                  <Button
                    sx={{ width: "100%" }}
                    color="secondary"
                    onClick={() => router.push("/request-access")}
                  >
                    {t("common:common.cancel")}
                  </Button>
                </>
              ) : null}

              {/* second step screen */}
              {activeStep === 1 ? (
                <>
                  <CustomerRequestReasonStep
                    reasonForRequestData={reasonForRequestData}
                    selectedReasons={selectedReasons}
                    setSelectedReasons={setSelectedReasons}
                    formik={requestedReasonFormik}
                  />
                  <Button
                    sx={{ width: "100%" }}
                    type="reset"
                    color="secondary"
                    onClick={() => handleBack()}
                  >
                    {t("common:common.back")}
                  </Button>
                </>
              ) : null}

              {/* third step screen */}
              {checkConditionalStep("FIBI", 2, 2, true) ? (
                <>
                  <CustomerBusinessUnitStep
                    reasonForRequestData={reasonForRequestData}
                    requestedReasons={requestedReasonFormik.values.requestReasons.map(
                      (e) => e.reason
                    )}
                    formik={customerBusinessUnitFormik}
                  />
                  <Button
                    sx={{ width: "100%" }}
                    type="reset"
                    color="secondary"
                    onClick={() => handleBack()}
                  >
                    {t("common:common.back")}
                  </Button>
                </>
              ) : null}

              {/* fourth step screen */}
              {checkConditionalStep("FIBI", 3, 2, false) ? (
                <>
                  <CustomerInformationStep formik={customerInformationFormik} />
                  <Button
                    sx={{ width: "100%" }}
                    type="reset"
                    color="secondary"
                    onClick={() => handleBack()}
                  >
                    {t("common:common.back")}
                  </Button>
                </>
              ) : null}

              {/* fifth/final step screen */}
              {checkConditionalStep("FIBI", 4, 3, false) ? (
                <>
                  <BusinessInformationStep formik={customerBusinessFormik} />
                  <Button
                    sx={{ width: "100%" }}
                    type="reset"
                    color="secondary"
                    onClick={() => handleBack()}
                  >
                    {t("common:common.back")}
                  </Button>
                </>
              ) : null}
              {checkConditionalStep("FIBI", 5, 4, false) ? (
                <>
                  <CustomerConfirmationStep
                    formData={getFormData()}
                    formik={customerSummaryFormik}
                    handleSetStep={handleSetStep}
                  />
                  <Button
                    sx={{ width: "100%" }}
                    type="reset"
                    color="secondary"
                    onClick={() => handleBack()}
                  >
                    {t("common:common.back")}
                  </Button>
                </>
              ) : null}
            </Box>
          </Box>
        )}
      </Wrapper>
    </>
  );
}

export async function getStaticProps(context: GetStaticPropsContext) {
  return {
    props: {
      ...(await serverSideTranslations(context.locale as string, [
        "common",
        "request_access",
      ])),
    },
  };
}
